import React from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { toCamelCaseLabel } from '../utils/camelCaseLabel';
import { webPointer } from '../utils/webLayout';

type Variant = 'primary' | 'secondary' | 'danger' | 'success' | 'text';
type Props = {
  title: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: Variant;
  accessibilityLabel?: string;
  style?: StyleProp<ViewStyle>;
  /** Override label color (e.g. dark text on a forced white CTA). */
  titleColor?: string;
};

export function AppButton({
  title,
  onPress,
  disabled = false,
  loading = false,
  variant = 'primary',
  accessibilityLabel,
  style,
  titleColor: titleColorOverride,
}: Props) {
  const palette = useAppColors();
  const blocked = disabled || loading;
  const isFilled =
    variant === 'primary' || variant === 'danger' || variant === 'success';

  const variantStyle =
    variant === 'primary'
      ? { backgroundColor: palette.brand, borderColor: palette.brand }
      : variant === 'secondary'
        ? { backgroundColor: palette.surface, borderColor: palette.border }
        : variant === 'danger'
          ? { backgroundColor: palette.error, borderColor: palette.error }
          : variant === 'success'
            ? { backgroundColor: palette.success, borderColor: palette.success }
            : {
                backgroundColor: palette.transparent,
                borderColor: palette.transparent,
              };

  const filledForeground =
    variant === 'danger'
      ? palette.onError
      : variant === 'success'
        ? palette.onSuccess
        : palette.white;
  const titleColor =
    titleColorOverride ??
    (isFilled
      ? filledForeground
      : variant === 'secondary'
        ? palette.textPrimary
        : palette.primary);
  const label = toCamelCaseLabel(title);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={
        accessibilityLabel
          ? toCamelCaseLabel(accessibilityLabel)
          : label
      }
      accessibilityState={{ disabled: blocked, busy: loading }}
      disabled={blocked}
      onPress={onPress}
      style={state => [
        styles.base,
        webPointer,
        variantStyle,
        // react-native-web reports hover; native never sets it.
        (state as { hovered?: boolean }).hovered && !blocked && styles.hovered,
        state.pressed && !blocked && styles.pressed,
        blocked && styles.disabled,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator
          testID="button-loader"
          color={titleColorOverride ?? (isFilled ? filledForeground : palette.primary)}
        />
      ) : (
        <Text style={[styles.title, { color: titleColor }]} numberOfLines={2}>
          {label}
        </Text>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    minHeight: 50,
    maxWidth: '100%',
    minWidth: 0,
    flexShrink: 1,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    borderWidth: 1,
  },
  hovered: { opacity: 0.9 },
  pressed: { opacity: 0.82 },
  disabled: { opacity: 0.45 },
  title: {
    ...typography.button,
    textAlign: 'center',
    flexShrink: 1,
  },
});
